/**
 * Configuración centralizada de tipos de documentos
 *
 * Para agregar un nuevo tipo de documento:
 * 1. Añade una entrada en DOCUMENT_TYPES_CONFIG
 * 2. Define los campos requeridos en fields
 * 3. El sistema automáticamente generará el formulario
 */

export interface DocumentField {
  key: string;
  label: string;
  placeholder: string;
  type: "text" | "date" | "number" | "textarea" | "email" | "tel";
  required?: boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  helpText?: string;
  group?: string; // Para agrupar campos relacionados
}

export interface DocumentTypeConfig {
  id: string;
  nameEs: string; // Nombre en español
  nameEn: string; // Nombre en inglés
  descriptionEs?: string; // Descripción en español
  descriptionEn?: string; // Descripción en inglés
  // Deprecated - mantener por compatibilidad
  name?: string;
  description?: string;
  fields: DocumentField[];
  category?: "contract" | "agreement" | "note" | "power" | "other";
  icon?: string;
}

export const DOCUMENT_TYPES_CONFIG: Record<string, DocumentTypeConfig> = {
  "contract-work": {
    id: "contract-work",
    nameEs: "Contrato de Trabajo",
    nameEn: "Employment Contract",
    descriptionEs:
      "Contrato laboral formal entre empleador y trabajador que establece las condiciones de empleo, incluyendo salario, horario, funciones y derechos laborales según la legislación vigente.",
    descriptionEn:
      "Formal employment agreement between employer and employee establishing employment conditions, including salary, schedule, duties and labor rights according to current legislation.",
    name: "Contrato de Trabajo",
    description:
      "Contrato laboral formal entre empleador y trabajador que establece las condiciones de empleo, incluyendo salario, horario, funciones y derechos laborales según la legislación vigente.",
    category: "contract",
    fields: [
      {
        key: "employerName",
        label: "Nombre del empleador",
        placeholder: "Nombre de la empresa o persona física",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "employerAddress",
        label: "Dirección del empleador",
        placeholder: "Dirección completa del empleador",
        type: "textarea",
        required: false,
        group: "parties",
        helpText: "Dirección fiscal y de contacto del empleador",
      },
      {
        key: "employerTaxId",
        label: "RFC / Número de identificación fiscal",
        placeholder: "Ej: ABC123456789",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Número de identificación fiscal del empleador",
      },
      {
        key: "employeeName",
        label: "Nombre del trabajador",
        placeholder: "Nombre completo del trabajador",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "employeeId",
        label: "Número de identificación del trabajador",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación oficial del trabajador",
      },
      {
        key: "employeeAddress",
        label: "Dirección del trabajador",
        placeholder: "Dirección completa del trabajador",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "position",
        label: "Puesto de trabajo",
        placeholder: "Cargo o posición a desempeñar",
        type: "text",
        required: true,
        group: "job",
      },
      {
        key: "jobDescription",
        label: "Descripción de funciones",
        placeholder:
          "Descripción detallada de las funciones y responsabilidades",
        type: "textarea",
        required: false,
        group: "job",
        helpText: "Funciones principales y responsabilidades del puesto",
      },
      {
        key: "workSchedule",
        label: "Jornada laboral",
        placeholder: "Ej: Lunes a Viernes, 9:00 AM - 6:00 PM",
        type: "text",
        required: false,
        group: "job",
        helpText: "Horario y días de trabajo",
      },
      {
        key: "salary",
        label: "Salario",
        placeholder: "Monto mensual o quincenal",
        type: "text",
        required: true,
        group: "job",
        helpText: "Incluye la moneda (ej: $50,000 MXN)",
      },
      {
        key: "paymentMethod",
        label: "Forma de pago",
        placeholder: "Ej: Transferencia bancaria, cheque, efectivo",
        type: "text",
        required: false,
        group: "job",
        helpText: "Método y frecuencia de pago",
      },
      {
        key: "startDate",
        label: "Fecha de inicio",
        placeholder: "Fecha de inicio del contrato",
        type: "date",
        required: true,
        group: "dates",
      },
      {
        key: "endDate",
        label: "Fecha de fin (opcional)",
        placeholder: "Fecha de terminación del contrato",
        type: "date",
        required: false,
        group: "dates",
        helpText: "Dejar vacío para contrato indefinido",
      },
      {
        key: "trialPeriod",
        label: "Periodo de prueba",
        placeholder: "Ej: 30 días, 3 meses",
        type: "text",
        required: false,
        group: "dates",
        helpText: "Duración del periodo de prueba si aplica",
      },
      {
        key: "benefits",
        label: "Beneficios adicionales (opcional)",
        placeholder: "Ej: Seguro médico, vacaciones, bonos, etc.",
        type: "textarea",
        required: false,
        group: "terms",
        helpText: "Beneficios adicionales al salario base",
      },
    ],
  },
  "contract-rent": {
    id: "contract-rent",
    nameEs: "Contrato de Arrendamiento",
    nameEn: "Rental Agreement",
    descriptionEs:
      "Contrato de alquiler de inmueble que establece los términos y condiciones entre arrendador y arrendatario, incluyendo renta, duración, obligaciones y derechos de ambas partes.",
    descriptionEn:
      "Property rental agreement establishing terms and conditions between landlord and tenant, including rent, duration, obligations and rights of both parties.",
    name: "Contrato de Arrendamiento",
    description:
      "Contrato de alquiler de inmueble que establece los términos y condiciones entre arrendador y arrendatario, incluyendo renta, duración, obligaciones y derechos de ambas partes.",
    category: "contract",
    fields: [
      {
        key: "landlordName",
        label: "Nombre del arrendador",
        placeholder: "Nombre completo del propietario o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "landlordId",
        label: "Identificación del arrendador",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del arrendador",
      },
      {
        key: "landlordAddress",
        label: "Dirección del arrendador",
        placeholder: "Dirección completa del arrendador",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "tenantName",
        label: "Nombre del arrendatario",
        placeholder: "Nombre completo del inquilino",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "tenantId",
        label: "Identificación del arrendatario",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del arrendatario",
      },
      {
        key: "tenantAddress",
        label: "Dirección del arrendatario",
        placeholder: "Dirección completa del arrendatario",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "propertyAddress",
        label: "Dirección del inmueble",
        placeholder: "Dirección completa del inmueble a arrendar",
        type: "textarea",
        required: true,
        group: "property",
      },
      {
        key: "propertyType",
        label: "Tipo de inmueble",
        placeholder: "Ej: Casa, Departamento, Local comercial, Oficina",
        type: "text",
        required: false,
        group: "property",
        helpText: "Tipo de propiedad a arrendar",
      },
      {
        key: "propertyDetails",
        label: "Detalles del inmueble",
        placeholder:
          "Descripción detallada: metros cuadrados, habitaciones, características, etc.",
        type: "textarea",
        required: false,
        group: "property",
        helpText: "Características y detalles del inmueble",
      },
      {
        key: "rent",
        label: "Renta mensual",
        placeholder: "Monto mensual de la renta",
        type: "text",
        required: true,
        group: "financial",
        helpText: "Incluye la moneda (ej: $15,000 MXN)",
      },
      {
        key: "deposit",
        label: "Depósito en garantía",
        placeholder: "Monto del depósito",
        type: "text",
        required: false,
        group: "financial",
        helpText: "Monto del depósito de garantía si aplica",
      },
      {
        key: "leaseTerm",
        label: "Duración del arrendamiento",
        placeholder: "Ej: 12 meses, 24 meses, 1 año",
        type: "text",
        required: true,
        group: "dates",
      },
      {
        key: "startDate",
        label: "Fecha de inicio",
        placeholder: "Fecha de inicio del arrendamiento",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "endDate",
        label: "Fecha de fin",
        placeholder: "Fecha de terminación del arrendamiento",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "usePurpose",
        label: "Uso del inmueble",
        placeholder: "Ej: Habitacional, Comercial, Oficina, Almacén",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Propósito para el cual se arrienda el inmueble",
      },
    ],
  },
  "contract-sale": {
    id: "contract-sale",
    nameEs: "Contrato de Compraventa",
    nameEn: "Sales Contract",
    descriptionEs:
      "Contrato de compra y venta de bienes muebles o inmuebles que establece las condiciones de la transacción, precio, forma de pago, entrega y garantías entre vendedor y comprador.",
    descriptionEn:
      "Purchase and sale contract for movable or immovable property establishing transaction conditions, price, payment method, delivery and warranties between seller and buyer.",
    name: "Contrato de Compraventa",
    description:
      "Contrato de compra y venta de bienes muebles o inmuebles que establece las condiciones de la transacción, precio, forma de pago, entrega y garantías entre vendedor y comprador.",
    category: "contract",
    fields: [
      {
        key: "sellerName",
        label: "Nombre del vendedor",
        placeholder: "Nombre completo del vendedor o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "sellerId",
        label: "Identificación del vendedor",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del vendedor",
      },
      {
        key: "sellerAddress",
        label: "Dirección del vendedor",
        placeholder: "Dirección completa del vendedor",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "buyerName",
        label: "Nombre del comprador",
        placeholder: "Nombre completo del comprador o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "buyerId",
        label: "Identificación del comprador",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del comprador",
      },
      {
        key: "buyerAddress",
        label: "Dirección del comprador",
        placeholder: "Dirección completa del comprador",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "itemDescription",
        label: "Descripción del bien",
        placeholder:
          "Descripción detallada del bien a vender (marca, modelo, características, estado, etc.)",
        type: "textarea",
        required: true,
        group: "item",
      },
      {
        key: "itemCondition",
        label: "Estado del bien",
        placeholder: "Ej: Nuevo, Usado en buen estado, Requiere reparación",
        type: "text",
        required: false,
        group: "item",
        helpText: "Condición actual del bien",
      },
      {
        key: "price",
        label: "Precio de venta",
        placeholder: "Monto total de la venta",
        type: "text",
        required: true,
        group: "financial",
        helpText: "Incluye la moneda (ej: $100,000 MXN)",
      },
      {
        key: "paymentMethod",
        label: "Método de pago",
        placeholder:
          "Ej: Efectivo, Transferencia bancaria, Cheque, Financiamiento",
        type: "text",
        required: true,
        group: "financial",
      },
      {
        key: "paymentSchedule",
        label: "Plan de pago (opcional)",
        placeholder: "Ej: Pago único, 3 pagos de $33,333, Mensualidades",
        type: "textarea",
        required: false,
        group: "financial",
        helpText: "Si el pago es a plazos, describe el plan",
      },
      {
        key: "deliveryDate",
        label: "Fecha de entrega",
        placeholder: "Fecha de entrega del bien",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "deliveryLocation",
        label: "Lugar de entrega",
        placeholder: "Dirección donde se entregará el bien",
        type: "textarea",
        required: false,
        group: "item",
        helpText: "Ubicación donde se realizará la entrega",
      },
      {
        key: "warranty",
        label: "Garantía (opcional)",
        placeholder: "Ej: 30 días, 6 meses, 1 año",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Periodo de garantía si aplica",
      },
    ],
  },
  "contract-service": {
    id: "contract-service",
    nameEs: "Contrato de Prestación de Servicios",
    nameEn: "Service Agreement",
    descriptionEs:
      "Contrato para la prestación de servicios profesionales o técnicos que establece el alcance del trabajo, honorarios, plazos y condiciones entre prestador y cliente.",
    descriptionEn:
      "Agreement for the provision of professional or technical services establishing scope of work, fees, deadlines and conditions between provider and client.",
    name: "Contrato de Prestación de Servicios",
    description:
      "Contrato para la prestación de servicios profesionales o técnicos que establece el alcance del trabajo, honorarios, plazos y condiciones entre prestador y cliente.",
    category: "contract",
    fields: [
      {
        key: "serviceProvider",
        label: "Nombre del prestador de servicios",
        placeholder: "Nombre completo del prestador o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "providerId",
        label: "Identificación del prestador",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del prestador",
      },
      {
        key: "providerAddress",
        label: "Dirección del prestador",
        placeholder: "Dirección completa del prestador",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "serviceClient",
        label: "Nombre del cliente",
        placeholder: "Nombre completo del cliente o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "clientId",
        label: "Identificación del cliente",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del cliente",
      },
      {
        key: "clientAddress",
        label: "Dirección del cliente",
        placeholder: "Dirección completa del cliente",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "serviceDescription",
        label: "Descripción del servicio",
        placeholder: "Descripción detallada de los servicios a prestar",
        type: "textarea",
        required: true,
        group: "service",
        helpText: "Alcance y especificaciones del servicio",
      },
      {
        key: "serviceType",
        label: "Tipo de servicio",
        placeholder:
          "Ej: Consultoría, Desarrollo de software, Diseño, Asesoría legal",
        type: "text",
        required: false,
        group: "service",
        helpText: "Categoría o tipo de servicio",
      },
      {
        key: "serviceFee",
        label: "Honorarios o precio",
        placeholder: "Monto total o por hora del servicio",
        type: "text",
        required: true,
        group: "financial",
        helpText: "Incluye la moneda (ej: $50,000 MXN o $500 USD/hora)",
      },
      {
        key: "paymentTerms",
        label: "Términos de pago",
        placeholder:
          "Ej: 50% anticipo y 50% al finalizar, Pago mensual, Pago único",
        type: "text",
        required: false,
        group: "financial",
        helpText: "Condiciones y forma de pago",
      },
      {
        key: "serviceDuration",
        label: "Duración del servicio",
        placeholder: "Ej: 3 meses, 6 semanas, Hasta completar el proyecto",
        type: "text",
        required: true,
        group: "dates",
      },
      {
        key: "startDate",
        label: "Fecha de inicio",
        placeholder: "Fecha de inicio de los servicios",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "endDate",
        label: "Fecha de finalización estimada",
        placeholder: "Fecha estimada de finalización",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "deliverables",
        label: "Entregables (opcional)",
        placeholder:
          "Ej: Informe final, Diseño completo, Código fuente, Documentación",
        type: "textarea",
        required: false,
        group: "service",
        helpText: "Productos o resultados que se entregarán",
      },
    ],
  },
  nda: {
    id: "nda",
    nameEs: "Acuerdo de Confidencialidad (NDA)",
    nameEn: "Non-Disclosure Agreement (NDA)",
    descriptionEs:
      "Acuerdo de no divulgación que protege información confidencial compartida entre partes, estableciendo obligaciones de confidencialidad, duración y consecuencias del incumplimiento.",
    descriptionEn:
      "Non-disclosure agreement protecting confidential information shared between parties, establishing confidentiality obligations, duration and consequences of breach.",
    name: "Acuerdo de Confidencialidad (NDA)",
    description:
      "Acuerdo de no divulgación que protege información confidencial compartida entre partes, estableciendo obligaciones de confidencialidad, duración y consecuencias del incumplimiento.",
    category: "agreement",
    fields: [
      {
        key: "disclosingParty",
        label: "Parte que divulga",
        placeholder: "Nombre de la parte que comparte información confidencial",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "disclosingPartyAddress",
        label: "Dirección de la parte que divulga",
        placeholder: "Dirección completa",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "receivingParty",
        label: "Parte que recibe",
        placeholder: "Nombre de la parte que recibe información confidencial",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "receivingPartyAddress",
        label: "Dirección de la parte que recibe",
        placeholder: "Dirección completa",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "purpose",
        label: "Propósito de la confidencialidad",
        placeholder:
          "Razón por la cual se comparte la información confidencial",
        type: "textarea",
        required: true,
        group: "terms",
        helpText: "Objetivo del acuerdo y contexto de la información",
      },
      {
        key: "confidentialInfo",
        label: "Información confidencial (opcional)",
        placeholder:
          "Descripción del tipo de información que se considera confidencial",
        type: "textarea",
        required: false,
        group: "terms",
        helpText: "Tipos de información que se protegerán",
      },
      {
        key: "duration",
        label: "Duración del acuerdo",
        placeholder: "Ej: 2 años, 5 años, Indefinido",
        type: "text",
        required: true,
        group: "terms",
        helpText: "Periodo durante el cual se mantiene la confidencialidad",
      },
      {
        key: "exceptions",
        label: "Excepciones (opcional)",
        placeholder:
          "Ej: Información pública, Información ya conocida, Información requerida por ley",
        type: "textarea",
        required: false,
        group: "terms",
        helpText: "Casos en los que no aplica la confidencialidad",
      },
    ],
  },
  "power-attorney": {
    id: "power-attorney",
    nameEs: "Poder Notarial",
    nameEn: "Power of Attorney",
    descriptionEs:
      "Documento que otorga poder a otra persona (apoderado) para actuar en representación del poderdante en asuntos específicos, con el alcance y duración definidos.",
    descriptionEn:
      "Document granting power to another person (attorney-in-fact) to act on behalf of the principal in specific matters, with defined scope and duration.",
    name: "Poder Notarial",
    description:
      "Documento que otorga poder a otra persona (apoderado) para actuar en representación del poderdante en asuntos específicos, con el alcance y duración definidos.",
    category: "power",
    fields: [
      {
        key: "principalName",
        label: "Nombre del poderdante",
        placeholder: "Nombre completo de la persona que otorga el poder",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "principalId",
        label: "Identificación del poderdante",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del poderdante",
      },
      {
        key: "principalAddress",
        label: "Dirección del poderdante",
        placeholder: "Dirección completa del poderdante",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "agentName",
        label: "Nombre del apoderado",
        placeholder: "Nombre completo de la persona que recibe el poder",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "agentId",
        label: "Identificación del apoderado",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del apoderado",
      },
      {
        key: "agentAddress",
        label: "Dirección del apoderado",
        placeholder: "Dirección completa del apoderado",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "powerType",
        label: "Tipo de poder",
        placeholder:
          "Ej: General, Especial, Para actos de dominio, Para pleitos y cobranzas",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Clasificación del tipo de poder otorgado",
      },
      {
        key: "powerScope",
        label: "Alcance del poder",
        placeholder:
          "Descripción detallada de las facultades y actos que puede realizar el apoderado",
        type: "textarea",
        required: true,
        group: "terms",
        helpText: "Facultades específicas otorgadas al apoderado",
      },
      {
        key: "powerDuration",
        label: "Duración del poder",
        placeholder: "Ej: 1 año, 2 años, Indefinido hasta revocación",
        type: "text",
        required: true,
        group: "terms",
        helpText: "Periodo de vigencia del poder",
      },
      {
        key: "revocation",
        label: "Condiciones de revocación (opcional)",
        placeholder:
          "Ej: Revocable en cualquier momento, Requiere notificación previa",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Condiciones para revocar el poder",
      },
    ],
  },
  "promissory-note": {
    id: "promissory-note",
    nameEs: "Pagaré",
    nameEn: "Promissory Note",
    descriptionEs:
      "Documento que reconoce una deuda y establece el compromiso de pago de una cantidad determinada en una fecha específica, incluyendo intereses si aplican.",
    descriptionEn:
      "Document acknowledging a debt and establishing commitment to pay a specific amount on a specific date, including interest if applicable.",
    name: "Pagaré",
    description:
      "Documento que reconoce una deuda y establece el compromiso de pago de una cantidad determinada en una fecha específica, incluyendo intereses si aplican.",
    category: "note",
    fields: [
      {
        key: "debtorName",
        label: "Nombre del deudor",
        placeholder: "Nombre completo de la persona que debe",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "debtorId",
        label: "Identificación del deudor",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del deudor",
      },
      {
        key: "debtorAddress",
        label: "Dirección del deudor",
        placeholder: "Dirección completa del deudor",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "creditorName",
        label: "Nombre del acreedor",
        placeholder: "Nombre completo de la persona a quien se debe",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "creditorId",
        label: "Identificación del acreedor",
        placeholder: "CURP, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del acreedor",
      },
      {
        key: "creditorAddress",
        label: "Dirección del acreedor",
        placeholder: "Dirección completa del acreedor",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "amount",
        label: "Monto a pagar",
        placeholder: "Cantidad total a pagar",
        type: "text",
        required: true,
        group: "financial",
        helpText: "Incluye la moneda (ej: $25,000 MXN)",
      },
      {
        key: "dueDate",
        label: "Fecha de vencimiento",
        placeholder: "Fecha límite de pago",
        type: "date",
        required: true,
        group: "dates",
      },
      {
        key: "paymentLocation",
        label: "Lugar de pago",
        placeholder: "Ej: Domicilio del acreedor, Banco específico, Dirección",
        type: "textarea",
        required: false,
        group: "financial",
        helpText: "Ubicación donde se realizará el pago",
      },
      {
        key: "interestRate",
        label: "Tasa de interés (opcional)",
        placeholder: "Ej: 5% anual, 1% mensual, Sin intereses",
        type: "text",
        required: false,
        group: "financial",
        helpText: "Tasa de interés aplicable si aplica",
      },
      {
        key: "paymentMethod",
        label: "Forma de pago",
        placeholder: "Ej: Efectivo, Transferencia, Cheque, Pago único",
        type: "text",
        required: false,
        group: "financial",
        helpText: "Método de pago acordado",
      },
      {
        key: "guarantor",
        label: "Aval o garante (opcional)",
        placeholder: "Nombre del aval o garante si aplica",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Persona que garantiza el pago",
      },
    ],
  },
  "lease-agreement": {
    id: "lease-agreement",
    nameEs: "Acuerdo de Arrendamiento Comercial",
    nameEn: "Commercial Lease Agreement",
    descriptionEs:
      "Contrato de arrendamiento para uso comercial o empresarial que establece términos específicos para negocios, incluyendo uso permitido, renovación, mejoras y condiciones comerciales.",
    descriptionEn:
      "Commercial or business lease agreement establishing specific terms for businesses, including permitted use, renewal, improvements and commercial conditions.",
    name: "Acuerdo de Arrendamiento Comercial",
    description:
      "Contrato de arrendamiento para uso comercial o empresarial que establece términos específicos para negocios, incluyendo uso permitido, renovación, mejoras y condiciones comerciales.",
    category: "contract",
    fields: [
      {
        key: "lessorName",
        label: "Nombre del arrendador",
        placeholder: "Nombre completo del propietario o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "lessorId",
        label: "Identificación del arrendador",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del arrendador",
      },
      {
        key: "lessorAddress",
        label: "Dirección del arrendador",
        placeholder: "Dirección completa del arrendador",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "lesseeName",
        label: "Nombre del arrendatario",
        placeholder: "Nombre completo del inquilino o empresa",
        type: "text",
        required: true,
        group: "parties",
      },
      {
        key: "lesseeId",
        label: "Identificación del arrendatario",
        placeholder: "RFC, DNI, Cédula, etc.",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Documento de identificación del arrendatario",
      },
      {
        key: "lesseeAddress",
        label: "Dirección del arrendatario",
        placeholder: "Dirección completa del arrendatario",
        type: "textarea",
        required: false,
        group: "parties",
      },
      {
        key: "businessName",
        label: "Nombre del negocio (opcional)",
        placeholder: "Nombre comercial del negocio a operar",
        type: "text",
        required: false,
        group: "parties",
        helpText: "Nombre del negocio que operará en el local",
      },
      {
        key: "propertyDetails",
        label: "Detalles del inmueble",
        placeholder:
          "Descripción completa del inmueble comercial (local, oficina, almacén, etc.)",
        type: "textarea",
        required: true,
        group: "property",
      },
      {
        key: "propertyAddress",
        label: "Dirección del inmueble",
        placeholder: "Dirección completa del inmueble comercial",
        type: "textarea",
        required: true,
        group: "property",
      },
      {
        key: "propertySize",
        label: "Tamaño del inmueble",
        placeholder: "Ej: 50 m², 200 m², 500 m²",
        type: "text",
        required: false,
        group: "property",
        helpText: "Superficie o área del inmueble",
      },
      {
        key: "permittedUse",
        label: "Uso permitido",
        placeholder:
          "Ej: Restaurante, Tienda de ropa, Oficina, Almacén, Consultorio médico",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Actividad comercial permitida en el inmueble",
      },
      {
        key: "leaseAmount",
        label: "Monto del arrendamiento",
        placeholder: "Renta mensual o quincenal",
        type: "text",
        required: true,
        group: "financial",
        helpText: "Incluye la moneda (ej: $30,000 MXN)",
      },
      {
        key: "deposit",
        label: "Depósito en garantía",
        placeholder: "Monto del depósito",
        type: "text",
        required: false,
        group: "financial",
        helpText: "Depósito de garantía si aplica",
      },
      {
        key: "leaseDuration",
        label: "Duración del arrendamiento",
        placeholder: "Ej: 24 meses, 36 meses, 5 años",
        type: "text",
        required: true,
        group: "dates",
      },
      {
        key: "startDate",
        label: "Fecha de inicio",
        placeholder: "Fecha de inicio del arrendamiento",
        type: "date",
        required: false,
        group: "dates",
      },
      {
        key: "renewalTerms",
        label: "Términos de renovación (opcional)",
        placeholder:
          "Ej: Renovable automáticamente, Requiere acuerdo previo, Opción de renovación",
        type: "text",
        required: false,
        group: "terms",
        helpText: "Condiciones para renovar el contrato",
      },
      {
        key: "improvements",
        label: "Mejoras permitidas (opcional)",
        placeholder:
          "Ej: Permite modificaciones con autorización, Sin modificaciones estructurales",
        type: "textarea",
        required: false,
        group: "terms",
        helpText: "Política sobre mejoras o modificaciones al inmueble",
      },
    ],
  },
};

/**
 * Obtiene la configuración de un tipo de documento
 */
export const getDocumentTypeConfig = (
  documentType: string
): DocumentTypeConfig | undefined => {
  return DOCUMENT_TYPES_CONFIG[documentType];
};

/**
 * Obtiene los tipos de documentos disponibles
 * @param language - Idioma para los nombres (es o en)
 */
export const getDocumentTypes = (
  language: "es" | "en" = "es"
): DocumentTypeConfig[] => {
  return Object.values(DOCUMENT_TYPES_CONFIG).map((type) => ({
    ...type,
    name: getDocumentTypeName(type.id, language),
    description: getDocumentTypeDescription(type.id, language),
  }));
};

/**
 * Obtiene el nombre de un tipo de documento en el idioma especificado
 */
export function getDocumentTypeName(
  documentType: string,
  language: "es" | "en" = "es"
): string {
  const config = DOCUMENT_TYPES_CONFIG[documentType];
  if (!config) return documentType;

  // Si tiene nameEs/nameEn usa esos
  if (language === "en" && config.nameEn) return config.nameEn;
  if (language === "es" && config.nameEs) return config.nameEs;

  // Fallback a name para compatibilidad
  return config.name || config.nameEs || documentType;
}

/**
 * Obtiene la descripción de un tipo de documento en el idioma especificado
 */
export function getDocumentTypeDescription(
  documentType: string,
  language: "es" | "en" = "es"
): string | undefined {
  const config = DOCUMENT_TYPES_CONFIG[documentType];
  if (!config) return undefined;

  // Si tiene descriptionEs/descriptionEn usa esos
  if (language === "en" && config.descriptionEn) return config.descriptionEn;
  if (language === "es" && config.descriptionEs) return config.descriptionEs;

  // Fallback a description para compatibilidad
  return config.description || config.descriptionEs;
}

/**
 * Obtiene los campos de un tipo de documento agrupados
 */
export const getDocumentFieldsByGroup = (
  documentType: string
): Record<string, DocumentField[]> => {
  const config = getDocumentTypeConfig(documentType);
  if (!config) return {};

  const grouped: Record<string, DocumentField[]> = {};
  config.fields.forEach((field) => {
    const group = field.group || "general";
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(field);
  });

  return grouped;
};

/**
 * Exporta en formato compatible con el código existente
 */
export const DOCUMENT_TYPES = Object.fromEntries(
  Object.entries(DOCUMENT_TYPES_CONFIG).map(([key, config]) => [
    key,
    config.name,
  ])
) as Record<string, string>;
